import { useCallback, useState } from 'react';
import { Box, Button, Card, Text, Title, Icon } from '@nimbus-ds/components';
import { CloseIcon } from '@nimbus-ds/icons';
import { useNavegate } from 'App/hooks';
import { Stack } from 'commons/components';
import { useToast } from 'commons/hooks';
import { NE_DISPLAY_SWITCHES } from 'domains/NuvemEnvioCommons/constants';
import useNuvemEnvioTranslations from 'domains/NuvemEnvioCommons/hooks/useNuvemEnvioTranslations/useNuvemEnvioTranslations';
import {
  trackingNuvemEnvioLoggiBannerActivate,
  trackingNuvemEnvioLoggiBannerCloseButton,
} from 'domains/NuvemEnvioCommons/tracking';
import { PARTNER_APPS_ROUTES } from 'domains/PartnersApps/partnersAppsRoutes';
import { useGetNuvemEnvioConfigs } from 'domains/Shipping/DeliveryMethods/hooks';
import useUpdateNuvemEnvioConfigs from 'domains/Shipping/DeliveryMethods/hooks/useUpdateNuvemEnvioConfigs';
import { ReactComponent as LoggiLogo } from '../../../images/LoggiLogo.svg';

function LoggiBanner() {
  const t = useNuvemEnvioTranslations();
  const { goTo } = useNavegate();
  const [isRequesting, setIsRequesting] = useState<boolean>(false);
  const { addToast } = useToast();

  const { updateNuvemEnvioDisplaySwitches } = useUpdateNuvemEnvioConfigs();
  const { fetchNuvemEnvioConfigs } = useGetNuvemEnvioConfigs();

  const handleGoToConfigurations = useCallback(() => {
    trackingNuvemEnvioLoggiBannerActivate();
    goTo(`${PARTNER_APPS_ROUTES.nuvemenvioConfigurations}/carrier/loggi`);
  }, [goTo]);

  const handleClose = useCallback(async () => {
    try {
      setIsRequesting(true);
      await updateNuvemEnvioDisplaySwitches(
        NE_DISPLAY_SWITCHES.SHOW_LOGGI_ACTIVATION_BANNER,
      );
      fetchNuvemEnvioConfigs();
    } catch (error) {
      addToast({
        label: t('shipping.banners.loggi.error'),
        appearance: 'danger',
      });
    } finally {
      setIsRequesting(false);
      trackingNuvemEnvioLoggiBannerCloseButton();
    }
  }, [fetchNuvemEnvioConfigs, updateNuvemEnvioDisplaySwitches, addToast, t]);

  return (
    <Card>
      <Card.Body>
        <Stack flex="0 1 auto">
          <LoggiLogo />

          <Box
            display="flex"
            flexDirection={{ xs: 'column', md: 'row' }}
            gap="2"
            width="100%"
          >
            <Box display="flex" flexDirection="column">
              <Box display="flex" gap="2" alignItems="flex-start">
                <Title as="h6">{t('shipping.banners.loggi.title')}</Title>
                <Box
                  display={{ xs: 'flex', md: 'none' }}
                  flexDirection="row"
                  gap="2"
                  alignItems="center"
                >
                  <Button
                    appearance="transparent"
                    onClick={handleClose}
                    disabled={isRequesting}
                    data-testid="banner-loggi-close-mobile"
                  >
                    <Icon source={<CloseIcon />} color="neutral-textDisabled" />
                  </Button>
                </Box>
              </Box>

              <Text>{t('shipping.banners.loggi.text')}</Text>
            </Box>
            <Box
              display="flex"
              flexDirection="row"
              gap="2"
              alignItems="center"
              ml={{ xs: 'none', md: 'auto' }}
            >
              <Box
                display="flex"
                flexDirection="row"
                gap="6"
                alignItems="center"
              >
                <Button
                  appearance="primary"
                  onClick={handleGoToConfigurations}
                  data-testid="banner-loggi-activate-button"
                >
                  {t('shipping.banners.loggi.link')}
                </Button>
              </Box>

              <Box display={{ xs: 'none', md: 'flex' }} alignItems="center">
                <Button
                  appearance="transparent"
                  onClick={handleClose}
                  disabled={isRequesting}
                  data-testid="banner-loggi-close-desktop"
                >
                  <Icon source={<CloseIcon />} color="neutral-textDisabled" />
                </Button>
              </Box>
            </Box>
          </Box>
        </Stack>
      </Card.Body>
    </Card>
  );
}

export default LoggiBanner;
